import React, { useState } from "react"

import Popup from "components/Ui/Popup"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import { DeactivateButton as UnpublishButton } from "./PublishWorkflow.styled"

type Props = {
  isPublished: boolean
  onPublish: () => void
  onUnpublish: () => void
  loading: boolean
  hasDraft?: boolean
  draftRevision?: number
  /** Revision number that will actually be published when the user confirms.
   *  Defaults to draftRevision. Set this when viewing an older revision so
   *  the button reads `Publish v{N}` and the confirm copy reflects the
   *  older-revision case. */
  targetRevision?: number
  /** True when the target revision is older than the latest draft —
   *  alters the confirmation copy. */
  isOlderRevision?: boolean
  /** The revision currently published. Shown on the Unpublish button so it
   *  is clear which version will stop processing orders. */
  publishedRevision?: number | null
}

const PublishWorkflow = ({
  isPublished,
  onPublish,
  onUnpublish,
  loading,
  hasDraft,
  draftRevision,
  targetRevision,
  isOlderRevision = false,
  publishedRevision
}: Props) => {
  const [openPopup, setOpenPopup] = useState<"publish" | "unpublish" | null>(null)

  const revisionToPublish = targetRevision ?? draftRevision
  const unpublishLabel =
    publishedRevision !== undefined && publishedRevision !== null
      ? `Unpublish v${publishedRevision}`
      : "Unpublish"

  if (isOlderRevision && revisionToPublish !== undefined) {
    return (
      <>
        <PrimaryButton handleClick={() => setOpenPopup("publish")}>
          Publish v{revisionToPublish}
        </PrimaryButton>
        {isPublished && (
          <UnpublishButton type="button" onClick={() => setOpenPopup("unpublish")}>
            {unpublishLabel}
          </UnpublishButton>
        )}
        {openPopup === "publish" && (
          <Popup
            title="Publish older version"
            subtitle={`This will publish revision ${revisionToPublish} (an older version) and replace the currently published version.`}
            handleCloseClick={(e) => {
              e.stopPropagation()
              setOpenPopup(null)
            }}
            handleOkClick={(e) => {
              e.stopPropagation()
              onPublish()
              setOpenPopup(null)
            }}
            buttonText="Yes, publish"
            loading={loading}
          />
        )}
        {openPopup === "unpublish" && (
          <Popup
            title="Unpublish workflow"
            subtitle="Are you sure you want to unpublish this workflow? It will stop processing new orders."
            handleCloseClick={(e) => {
              e.stopPropagation()
              setOpenPopup(null)
            }}
            handleOkClick={(e) => {
              e.stopPropagation()
              onUnpublish()
              setOpenPopup(null)
            }}
            buttonText="Yes, unpublish"
            loading={loading}
          />
        )}
      </>
    )
  }

  if (isPublished && hasDraft) {
    return (
      <>
        <PrimaryButton handleClick={() => setOpenPopup("publish")}>
          Publish v{draftRevision}
        </PrimaryButton>
        <UnpublishButton type="button" onClick={() => setOpenPopup("unpublish")}>
          {unpublishLabel}
        </UnpublishButton>
        {openPopup === "publish" && (
          <Popup
            title="Publish new version"
            subtitle={`This will publish revision ${draftRevision} and replace the currently published version.`}
            handleCloseClick={(e) => {
              e.stopPropagation()
              setOpenPopup(null)
            }}
            handleOkClick={(e) => {
              e.stopPropagation()
              onPublish()
              setOpenPopup(null)
            }}
            buttonText="Yes, publish"
            loading={loading}
          />
        )}
        {openPopup === "unpublish" && (
          <Popup
            title="Unpublish workflow"
            subtitle="Are you sure you want to unpublish this workflow? It will stop processing new orders."
            handleCloseClick={(e) => {
              e.stopPropagation()
              setOpenPopup(null)
            }}
            handleOkClick={(e) => {
              e.stopPropagation()
              onUnpublish()
              setOpenPopup(null)
            }}
            buttonText="Yes, unpublish"
            loading={loading}
          />
        )}
      </>
    )
  }

  if (isPublished) {
    return (
      <>
        <UnpublishButton type="button" onClick={() => setOpenPopup("unpublish")}>
          {unpublishLabel}
        </UnpublishButton>
        {openPopup === "unpublish" && (
          <Popup
            title="Unpublish workflow"
            subtitle="Are you sure you want to unpublish this workflow? It will stop processing new orders."
            handleCloseClick={(e) => {
              e.stopPropagation()
              setOpenPopup(null)
            }}
            handleOkClick={(e) => {
              e.stopPropagation()
              onUnpublish()
              setOpenPopup(null)
            }}
            buttonText="Yes, unpublish"
            loading={loading}
          />
        )}
      </>
    )
  }

  return (
    <>
      <PrimaryButton handleClick={() => setOpenPopup("publish")}>
        Publish
      </PrimaryButton>
      {openPopup === "publish" && (
        <Popup
          title="Publish workflow"
          subtitle="Publishing will activate this workflow. It will start processing new orders immediately."
          handleCloseClick={(e) => {
            e.stopPropagation()
            setOpenPopup(null)
          }}
          handleOkClick={(e) => {
            e.stopPropagation()
            onPublish()
            setOpenPopup(null)
          }}
          buttonText="Yes, publish"
          loading={loading}
        />
      )}
    </>
  )
}

export default PublishWorkflow
